import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { MdAddAPhoto, MdCameraAlt, MdSave, MdStopCircle, MdDelete } from "react-icons/md";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import firebase from "firebase/compat/app";
import "firebase/compat/storage";
import { AuthContext } from "../../../contexts/AuthContext";
import { tempUrl, useStateContext } from "../../../contexts/ContextProvider";
import { ConfirmDeleteDialog, Loader, ScrollToTop } from "../../../components";

const TambahDataVerifikasi = () => {
  const { user, dispatch } = useContext(AuthContext);
  const { screenSize } = useStateContext();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [admin, setAdmin] = useState("");
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [openAdmin, setOpenAdmin] = useState(false);
  let [pageAdmin, setPageAdmin] = useState(0);
  const [limitAdmin] = useState(10);
  const [pagesAdmin, setPagesAdmin] = useState(0);
  const [queryAdmin, setQueryAdmin] = useState("");
  const [searchTermAdmin, setSearchTermAdmin] = useState("");
  const [fotoKunjungan, setFotoKunjungan] = useState([]);
  const [previewKunjungan, setPreviewKunjungan] = useState([]);
  const [alasan, setAlasan] = useState("");
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [currentLocation, setCurrentLocation] = useState(null);
  const [deleteFotoIndex, setDeleteFotoIndex] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getAdmins();
  }, [pageAdmin, searchTermAdmin]);

  useEffect(() => {
    return () => {
      stopCamera(false);
    };
  }, []);

  const handleAuthError = (err) => {
    if (err.response?.status === 401) {
      dispatch({ type: "LOGOUT" });
      navigate("/");
    }
  };

  const getAdmins = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${tempUrl}/dataPermohonansPagination?available_data_verifikasi=true&search_query=${encodeURIComponent(searchTermAdmin)}&page=${pageAdmin}&limit=${limitAdmin}`,
        {
          id: user._id,
          token: user.token,
        },
      );
      setQueryAdmin(searchTermAdmin);
      setAdmins(response.data.dataPermohonans || response.data.admins || []);
      setPageAdmin(response.data.page);
      setPagesAdmin(response.data.totalPage);
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || "Gagal mengambil data admin.");
    }
    setLoading(false);
  };

  const adminHandler = (value) => {
    const pickedAdmin = admins.find((item) => item._id === value);
    setAdmin(value);
    setSelectedAdmin(pickedAdmin || null);
    setOpenAdmin(false);
  };

  const handleChangeAdmin = (e, p) => {
    setPageAdmin(p - 1);
  };

  const searchDataAdmin = (event) => {
    event.preventDefault();
    setPageAdmin(0);
    setSearchTermAdmin(queryAdmin);
  };

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error("Browser tidak mendukung akses lokasi."));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            capturedAt: new Date().toISOString(),
          });
        },
        () => reject(new Error("Lokasi/GPS wajib aktif sebelum membuka kamera.")),
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 0,
        },
      );
    });
  };

  const waitForVideoElement = () => {
    return new Promise((resolve) => {
      const findVideo = () => {
        if (videoRef.current) {
          resolve(videoRef.current);
          return;
        }

        setTimeout(findVideo, 30);
      };

      findVideo();
    });
  };

  const playCameraStream = async (stream) => {
    const video = await waitForVideoElement();
    video.srcObject = stream;

    try {
      await video.play();
    } catch (err) {
      const isInterruptedPlay =
        err.name === "AbortError" ||
        err.message?.includes("interrupted by a new load request");

      if (!isInterruptedPlay) {
        throw err;
      }

      setTimeout(() => {
        if (video.srcObject === stream) {
          video.play().catch(() => {});
        }
      }, 120);
    }
  };

  const startCamera = async () => {
    try {
      stopCamera(false);
      setCameraError("");

      const location = await getCurrentLocation();
      setCurrentLocation(location);

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCameraError("Browser tidak mendukung akses kamera.");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
        },
        audio: false,
      });

      streamRef.current = stream;
      await playCameraStream(stream);
      setCameraActive(true);
    } catch (err) {
      setCameraError(err.message || "Kamera tidak bisa dibuka. Pastikan izin kamera dan lokasi diberikan.");
      console.log(err);
    }
  };

  const stopCamera = (updateState = true) => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    if (updateState) {
      setCameraActive(false);
      setCurrentLocation(null);
    }
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) {
      return;
    }

    let latestLocation = currentLocation;

    try {
      latestLocation = await getCurrentLocation();
      setCurrentLocation(latestLocation);
    } catch (err) {
      setCameraError("Lokasi/GPS wajib aktif sebelum mengambil foto.");
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);

    canvas.toBlob(
      (blob) => {
        const capturedFile = new File([blob], `foto-kunjungan-${Date.now()}.jpg`, {
          type: "image/jpeg",
        });

        setFotoKunjungan((prevFiles) => [
          ...prevFiles,
          {
            file: capturedFile,
            latitude: latestLocation.latitude,
            longitude: latestLocation.longitude,
            accuracy: latestLocation.accuracy,
            capturedAt: latestLocation.capturedAt,
          },
        ]);
        setPreviewKunjungan((prevPreviews) => [...prevPreviews, URL.createObjectURL(capturedFile)]);
        setCameraError("");
      },
      "image/jpeg",
      0.9,
    );
  };

  const uploadFotoGroup = async (files) => {
    const uploadedImageUrls = await Promise.all(
      files.map(async (selectedPhoto) => {
        const selectedFile = selectedPhoto.file || selectedPhoto;
        const storageRef = firebase.storage().ref();
        const fileRef = storageRef.child(
          `${Date.now()}-${Math.floor(Math.random() * 1000 + 1)}-${selectedFile.name}`,
        );
        const snapshot = await fileRef.put(selectedFile);
        const url = await snapshot.ref.getDownloadURL();

        return {
          url,
          latitude: selectedPhoto.latitude,
          longitude: selectedPhoto.longitude,
          accuracy: selectedPhoto.accuracy,
          capturedAt: selectedPhoto.capturedAt,
        };
      }),
    );

    return uploadedImageUrls;
  };

  const hapusFotoBaru = () => {
    if (deleteFotoIndex == null) {
      return;
    }

    setFotoKunjungan(fotoKunjungan.filter((item, i) => i !== deleteFotoIndex));
    setPreviewKunjungan(previewKunjungan.filter((item, i) => i !== deleteFotoIndex));
    setDeleteFotoIndex(null);
  };

  const saveDataVerifikasi = async (event) => {
    event.preventDefault();

    if (!admin || !alasan) {
      setError("Data Permohonan dan alasan wajib diisi.");
      return;
    }

    if (fotoKunjungan.length === 0) {
      setError("Foto kunjungan wajib diisi.");
      return;
    }

    setLoading(true);
    try {
      const fotoKunjunganUploaded = await uploadFotoGroup(fotoKunjungan);

      await axios.post(`${tempUrl}/saveDataVerifikasi`, {
        admin,
        fotoKunjungan: fotoKunjunganUploaded,
        alasan,
        id: user._id,
        token: user.token,
      });
      navigate("/dataVerifikasi");
    } catch (err) {
      handleAuthError(err);
      setError(err.response?.data?.message || "Gagal menyimpan Data Verifikasi.");
    }
    setLoading(false);
  };

  const renderDialogAdmin = () => {
    return (
      <Dialog open={openAdmin} onClose={() => setOpenAdmin(false)} maxWidth="md" fullWidth>
        <DialogTitle>{`Pilih Data Permohonan`}</DialogTitle>
        <DialogContent>
          <Form onSubmit={searchDataAdmin}>
            <div className="search-row mt-0 mb-3">
              <Form.Control
                value={queryAdmin}
                placeholder="Cari nama pemohon, no jual, nopol, atau alamat..."
                onChange={(e) => setQueryAdmin(e.target.value)}
              />
              <Button type="submit">Cari</Button>
            </div>
          </Form>
          <TableContainer component={Paper}>
            <Table aria-label="pilih admin">
              <TableHead>
                <TableRow>
                  <TableCell>Nama Pemohon</TableCell>
                  <TableCell>No Jual</TableCell>
                  <TableCell>Nopol</TableCell>
                  <TableCell>Alamat</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {admins.map((item) => (
                  <TableRow
                    key={item._id}
                    className="row-clickable"
                    onClick={() => adminHandler(item._id)}
                  >
                    <TableCell>{item.namaPemohon?.permohonan?.namaPemohon || "-"}</TableCell>
                    <TableCell>{item.noJual || "-"}</TableCell>
                    <TableCell>{item.nopol || "-"}</TableCell>
                    <TableCell>{item.alamat || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <div className="search-row">
            <Pagination
              count={pagesAdmin}
              page={pageAdmin + 1}
              onChange={handleChangeAdmin}
              color="primary"
              size={screenSize <= 600 ? "small" : "medium"}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button className="btn-back" variant="outline-secondary" type="button" onClick={() => setOpenAdmin(false)}>
            {"< Kembali"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      <ScrollToTop />
      <div className="page-header">
        <div>
          <div className="page-kicker">Survey</div>
          <h1 className="page-title">Tambah Data Verifikasi</h1>
        </div>
      </div>
      <section className="section-panel">
        {error && <Alert variant="warning">{error}</Alert>}
        <Form onSubmit={saveDataVerifikasi}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Data Permohonan</Form.Label>
                <Form.Control
                  value={selectedAdmin?.namaPemohon?.permohonan?.namaPemohon || ""}
                  placeholder="PILIH ADMIN"
                  readOnly
                  onClick={() => setOpenAdmin(true)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>No Jual</Form.Label>
                <Form.Control value={selectedAdmin?.noJual || ""} disabled readOnly />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Nopol</Form.Label>
                <Form.Control value={selectedAdmin?.nopol || ""} disabled readOnly />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Alamat</Form.Label>
                <Form.Control as="textarea" rows={3} value={selectedAdmin?.alamat || ""} disabled readOnly />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Alasan</Form.Label>
                <Form.Control as="textarea" rows={3} value={alasan} onChange={(e) => setAlasan(e.target.value)} />
              </Form.Group>
            </Col>
          </Row>

          <section className="section-panel mt-3">
            <h2 className="h6 fw-bold mb-3">Foto Kunjungan</h2>
            {cameraError && <Alert variant="warning">{cameraError}</Alert>}
            <div className="camera-box">
              <video
                ref={videoRef}
                playsInline
                muted
                className="camera-preview"
                style={{ display: cameraActive ? "block" : "none" }}
              />
              <canvas ref={canvasRef} style={{ display: "none" }} />
            </div>
            <div className="form-actions mb-3">
              <Button className="btn-icon" type="button" onClick={startCamera} disabled={cameraActive}>
                <MdCameraAlt /> Buka Kamera
              </Button>
              <Button className="btn-icon" type="button" variant="success" onClick={capturePhoto} disabled={!cameraActive}>
                <MdAddAPhoto /> Ambil Foto
              </Button>
              <Button className="btn-icon" type="button" variant="outline-secondary" onClick={stopCamera} disabled={!cameraActive}>
                <MdStopCircle /> Tutup Kamera
              </Button>
            </div>
            {previewKunjungan.length > 0 && <div className="detail-label mb-2">Foto Baru</div>}
            <div className="photo-grid">
              {previewKunjungan.map((img, i) => (
                <div className="photo-card old-photo-card" key={`foto-kunjungan-new-${i}`}>
                  <button type="button">
                    <img src={img} alt={`Foto Kunjungan baru ${i + 1}`} />
                    <span>No. {i + 1}</span>
                  </button>
                  <Button className="photo-delete-btn" size="sm" variant="outline-danger" type="button" onClick={() => setDeleteFotoIndex(i)}>
                    <MdDelete /> Hapus
                  </Button>
                </div>
              ))}
            </div>
          </section>

          <div className="form-actions mt-3">
            <Button className="btn-back" type="button" variant="outline-secondary" onClick={() => navigate("/dataVerifikasi")}>
              {"< Kembali"}
            </Button>
            <Button className="btn-icon" type="submit">
              <MdSave /> Simpan
            </Button>
          </div>
        </Form>
      </section>
      {renderDialogAdmin()}
      <ConfirmDeleteDialog
        show={deleteFotoIndex != null}
        message="Yakin ingin menghapus foto ini?"
        onCancel={() => setDeleteFotoIndex(null)}
        onConfirm={hapusFotoBaru}
      />
    </div>
  );
};

export default TambahDataVerifikasi;

